import * as IF from "infrontjs";
import template from "./template.html";

import { Weather } from "../../../core/Weather";
import { _lcs } from "../../../i18n";
import { guardBack } from "../../../core/BackDismiss";

/**
 * <weather-sheet> — the forecast detail dialog.
 *
 * A bottom sheet (the reachable place for a dialog on a phone), opened from
 * either statcard on the home screen via View.openWeather( mode ). Two views
 * share one panel:
 *
 *   - "hours"    — the hour-by-hour rail, opened by tapping the temperature.
 *   - "forecast" — current detail metrics + the 7-day outlook, opened by the
 *                  "Vorhersage · 7 Tage & Details" button.
 *
 * A tab pair switches between them, so neither entry point is a dead end.
 *
 * Everything is built from whatever the core-api actually delivered: metric
 * tiles are only emitted for values that are present, because MET Norway's
 * coverage varies by region (wind gusts are absent over Central Europe, UV runs
 * out after ~3 days). A tile reading "0" where there is no measurement would be
 * a lie; a missing tile is merely quiet.
 */

const MODES = [ "hours", "forecast" ];

/**
 * Detail tiles, in display order. Each maps a field of `current` to a label and
 * icon; `format` returns null to omit the tile entirely. The labels are German
 * source strings that double as their dictionary keys — this is a module
 * constant, evaluated before I18n exists, so _metrics() translates them.
 */
const METRICS = [
    {
        key    : "windSpeed",
        icon   : "wind",
        label  : "Wind",
        format : ( value, current ) =>
        {
            const compass = Weather.compass( current.windDirection );
            const speed = Math.round( value ) + " km/h";
            return compass ? _lcs( "{speed} aus {compass}", { speed : speed, compass : compass } ) : speed;
        }
    },
    { key: "windGust",      icon: "wind",       label: "Böen",        format: ( v ) => Math.round( v ) + " km/h" },
    { key: "precipitation", icon: "umbrella",   label: "Niederschlag", format: ( v ) => Weather.number( v, 1 ) + " mm" },
    { key: "humidity",      icon: "droplets",   label: "Luftfeuchte", format: ( v ) => Math.round( v ) + " %" },
    { key: "cloudCover",    icon: "cloud",      label: "Bewölkung",   format: ( v ) => Math.round( v ) + " %" },
    { key: "pressure",      icon: "gauge",      label: "Luftdruck",   format: ( v ) => Math.round( v ) + " hPa" },
    { key: "uvIndex",       icon: "sun-medium", label: "UV-Index",    format: ( v ) => Weather.number( v, 1 ) },
    { key: "dewPoint",      icon: "thermometer", label: "Taupunkt",   format: ( v ) => Weather.number( v, 1 ) + " °C" }
];

class WeatherSheet extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
        this._open = false;
        this._mode = "forecast";
        this._onKeydown = ( e ) => { if ( "Escape" === e.key ) this.close(); };
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this._connected = true;
        }
    }

    disconnectedCallback()
    {
        document.removeEventListener( "keydown", this._onKeydown );
    }

    render()
    {
        this.app.view.render( this, template, this._viewModel() );

        this.sheet = this.querySelector( '[data-id="sheet"]' );
        this.bind();
    }

    bind()
    {
        this.querySelector( '[data-id="scrim"]' )?.addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="close"]' )?.addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="grip"]' )?.addEventListener( "click", () => this.close() );

        this.querySelectorAll( ".wtab" ).forEach( ( tab ) =>
        {
            tab.addEventListener( "click", () => this.setMode( tab.getAttribute( "data-mode" ) ) );
        } );
    }

    /**
     * @param {('hours'|'forecast')} [mode] Which view to open on. Defaults to the
     *        last one used.
     */
    open( mode = null )
    {
        if ( MODES.includes( mode ) )
        {
            this._mode = mode;
        }

        // Re-render on open so the sheet always reflects the latest forecast
        // (the home screen may have refreshed it since last time).
        this.render();
        if ( !this._open )
        {
            // Back closes the sheet rather than leaving the page (F19).
            this._releaseBack = guardBack( () => this.close( true ) );
        }
        this._open = true;
        document.addEventListener( "keydown", this._onKeydown );

        requestAnimationFrame( () =>
        {
            this.sheet?.classList.add( "wsheet--open" );
            this.sheet?.setAttribute( "aria-hidden", "false" );
        } );
    }

    /** Swap views in place while the sheet stays open. */
    setMode( mode )
    {
        if ( !MODES.includes( mode ) || mode === this._mode )
        {
            return;
        }

        this._mode = mode;
        const wasOpen = this._open;
        this.render();

        if ( wasOpen )
        {
            // Synchronously, not in a rAF: the freshly rendered panel must already
            // carry the open class when the browser first computes its style, or
            // it would animate out from under the guest and slide back in.
            this.sheet?.classList.add( "wsheet--open" );
            this.sheet?.setAttribute( "aria-hidden", "false" );
        }
    }

    /** @param {boolean} [byBack] Closed by the back button (BackDismiss). */
    close( byBack = false )
    {
        if ( !this._open ) return;
        this._open = false;
        if ( !byBack && this._releaseBack )
        {
            this._releaseBack();
        }
        this._releaseBack = null;
        document.removeEventListener( "keydown", this._onKeydown );
        this.sheet?.classList.remove( "wsheet--open" );
        this.sheet?.setAttribute( "aria-hidden", "true" );
    }

    // ------------------------------------------------------------------

    _viewModel()
    {
        const forecast = this.app.weather.peek();
        const current = forecast ? forecast.current : null;
        const location = ( forecast && forecast.location ) ? forecast.location : {};

        // Prefer the API's own wall-clock string: it is the accommodation's local
        // time, whereas Weather.time() would render in the guest's device
        // timezone — wrong for anyone travelling from another one.
        const updated = current ? ( current.localTime || Weather.time( current.time ) ) : null;
        const place = location.name || null;

        return {
            mode     : this._mode,
            title    : this.app.accommodation?.name || place || _lcs( "Wetter" ),
            subtitle : [ place, updated ? _lcs( "Aktualisiert {time} Uhr", { time : updated } ) : null ]
                .filter( Boolean ).join( " · " ) || _lcs( "Wetter" ),
            current  : current ? {
                icon  : Weather.icon( current.symbol ),
                temp  : Weather.temp( current.temperature ),
                label : Weather.label( current.symbol )
            } : null,
            metrics     : current ? WeatherSheet._metrics( current ) : [],
            hours       : WeatherSheet._hours( forecast ? forecast.hours : [] ),
            days        : WeatherSheet._days( forecast ? forecast.days : [] ),
            // Only said when it is true: the forecast is for the region's centre
            // because this accommodation has no coordinates on file.
            regionHint  : "region" === location.source
                ? ( place
                    ? _lcs( "Regionale Vorhersage für {place}.", { place : place } )
                    : _lcs( "Regionale Vorhersage." ) )
                : null,
            attribution : ( forecast && forecast.attribution ) ? forecast.attribution : Weather.FALLBACK_ATTRIBUTION
        };
    }

    /** Tiles for the metrics upstream actually delivered. */
    static _metrics( current )
    {
        const tiles = [];

        for ( const metric of METRICS )
        {
            const value = current[ metric.key ];
            if ( null === value || undefined === value || !Number.isFinite( +value ) )
            {
                continue;
            }

            const formatted = metric.format( +value, current );
            if ( null === formatted || undefined === formatted )
            {
                continue;
            }

            tiles.push( { icon: metric.icon, label: _lcs( metric.label ), value: formatted } );
        }

        return tiles;
    }

    /**
     * Cells for the hourly rail. The window rolls past midnight, so wherever the
     * local date changes the cell carries a `dayBreak` label ("Morgen", or the
     * weekday) and the template draws a divider before it.
     */
    static _hours( hours )
    {
        const list = hours || [];
        if ( 0 === list.length )
        {
            return [];
        }

        // The first entry is the hour in progress, so its date is "today" as far
        // as the accommodation's timezone is concerned.
        const firstDate = list[ 0 ].date;
        let previousDate = null;

        return list.map( ( h ) =>
        {
            const isNewDay = null !== previousDate && h.date !== previousDate;
            previousDate = h.date;

            return {
                isNow    : true === h.isNow,
                time     : h.isNow ? _lcs( "Jetzt" ) : ( h.localTime || "" ),
                icon     : Weather.icon( h.symbol ),
                label    : Weather.label( h.symbol ),
                temp     : Weather.temp( h.temperature ),
                rain     : ( Number.isFinite( +h.precipitation ) && +h.precipitation > 0 )
                    ? Weather.number( h.precipitation, 1 )
                    : null,
                dayBreak : isNewDay ? Weather.relativeDay( h.date, firstDate ) : null
            };
        } );
    }

    /**
     * Forecast rows, each carrying the geometry of its temperature bar: the bars
     * share one scale across the whole week, so a cool day reads as visibly
     * shorter and lower than a warm one.
     */
    static _days( days )
    {
        const usable = ( days || [] ).filter( ( d ) => d && Number.isFinite( +d.tempMin ) && Number.isFinite( +d.tempMax ) );
        if ( 0 === usable.length )
        {
            return [];
        }

        const weekMin = Math.min( ...usable.map( ( d ) => +d.tempMin ) );
        const weekMax = Math.max( ...usable.map( ( d ) => +d.tempMax ) );
        const span = weekMax - weekMin;

        return usable.map( ( d ) =>
        {
            // A flat week (span 0) would divide by zero — show full-width bars.
            const left = span > 0 ? ( ( +d.tempMin - weekMin ) / span ) * 100 : 0;
            const width = span > 0 ? ( ( +d.tempMax - +d.tempMin ) / span ) * 100 : 100;

            return {
                isToday   : true === d.isToday,
                weekday   : Weather.weekday( d.date, d.isToday ),
                dayMonth  : Weather.dayMonth( d.date ),
                icon      : Weather.icon( d.symbol ),
                label     : Weather.label( d.symbol ),
                // Only worth a raindrop when there is measurable rain.
                rain      : ( Number.isFinite( +d.precipitation ) && +d.precipitation > 0 )
                    ? Weather.number( d.precipitation, 1 ) + " mm"
                    : null,
                min       : Weather.temp( d.tempMin ),
                max       : Weather.temp( d.tempMax ),
                barLeft   : Math.max( 0, Math.min( 100, left ) ).toFixed( 1 ),
                // Keep a sliver visible when min and max coincide.
                barWidth  : Math.max( 6, Math.min( 100 - left, width ) ).toFixed( 1 )
            };
        } );
    }
}

customElements.define( "weather-sheet", WeatherSheet );
